import { useRef, useState } from 'react'

import { clsx } from 'clsx'
import { nanoid } from 'nanoid'

import { Button } from '../Button/index.js'
import { CreateConfirmationOptions } from '../Dialog/ConfirmDialog.js'
import { confirm } from '../Dialog/index.js'
import { useDocument } from '../hooks/useDocument.js'
import { SvgIcon } from '../Icon/SvgIcon.js'
import { SortableList, DragResult } from '../SortableList/index.js'
import { LabelPickerItem } from './LabelPickerItem.js'
import { ColoredLabel } from './types.js'
import { colorStylesFactory, randomColor } from './utils.js'

export type LabelPickerProps = {
  value: ColoredLabel[]
  onChange?: (newLabel: ColoredLabel[]) => void
  placeholder?: string
  buttonLabel?: string
  invalid?: boolean
  disabled?: boolean
  itemCancelLabel?: string
  deleteConfirmation?: CreateConfirmationOptions
}

export const LabelPicker = ({
  value,
  onChange,
  placeholder,
  buttonLabel,
  invalid = false,
  disabled = false,
  itemCancelLabel,
  deleteConfirmation,
}: LabelPickerProps) => {
  const { document } = useDocument()

  const activatorNode = useRef<HTMLButtonElement>(null)
  const [activeIndex, setActiveIndex] = useState<number | undefined>(undefined)
  const [isNewItem, setNewItem] = useState(false)
  const [items, setItems] = useState<ColoredLabel[]>(value ?? [])
  const colorStyles = colorStylesFactory(document)

  const handleChange = (newItems: ColoredLabel[]) => {
    onChange?.(newItems)
    setActiveIndex(undefined)
    setNewItem(false)
  }

  const addItem = () => {
    setItems(items => [
      ...items,
      {
        id: nanoid(),
        name: '',
        colorHex: randomColor(colorStyles, items.length),
      },
    ])
    setActiveIndex(items.length)
    setNewItem(true)
  }

  const updateItem = (index: number, changes: Record<string, unknown>) => {
    const newItems = items.map((item, idx) =>
      idx === index ? { ...item, ...changes } : item,
    )
    setItems(newItems)
    handleChange(newItems)
    focusActivatorNode()
  }

  const onRemove = async (index: number) => {
    if (deleteConfirmation && !isNewItem) {
      const confirmed = await confirm(deleteConfirmation)
      if (!confirmed) {
        return
      }
    }

    const newItems = [...items.slice(0, index), ...items.slice(index + 1)]
    setItems(newItems)
    handleChange(newItems)
  }

  const onDragEnd = (result: DragResult) => {
    if (!result.destination) {
      return
    }

    const newItems = SortableList.reorder(
      items,
      result.source.index,
      result.destination.index,
    )

    setItems(newItems)
    handleChange(newItems)
  }

  const focusActivatorNode = () => {
    requestAnimationFrame(() => {
      if (activatorNode.current == null) {
        return
      }

      activatorNode.current.focus()
    })
  }

  return (
    <div className="flex flex-col space-y-3">
      <SortableList
        items={items}
        onDragEnd={onDragEnd}
        disabled={disabled || activeIndex !== undefined}
        renderItem={(item, index) => (
          <SortableList.Item id={item.id}>
            <div className="flex items-center space-s-2">
              <SortableList.Handle />
              <div
                className={clsx(
                  'relative',
                  'grow truncate border border-line-subdued rounded-base p-2 px-3 bg-surface hover:bg-surface-hovered',
                  'transition duration-200',
                )}
              >
                <LabelPickerItem
                  item={item}
                  active={activeIndex === index}
                  onEdit={value => updateItem(index, value)}
                  onRemove={() => onRemove(index)}
                  onCancel={() => {
                    if (isNewItem) {
                      onRemove(index)
                    }
                    setActiveIndex(undefined)
                    focusActivatorNode()
                  }}
                  setActive={active => {
                    setActiveIndex(active ? index : undefined)
                  }}
                  placeholder={placeholder}
                  disabled={
                    disabled ||
                    (activeIndex !== undefined && activeIndex !== index)
                  }
                  cancelLabel={itemCancelLabel}
                />
              </div>
            </div>
          </SortableList.Item>
        )}
      />
      <Button
        variant="secondaryNeutral"
        ref={activatorNode}
        onClick={addItem}
        fullWidth
        leadingIcon={<SvgIcon name="plus" />}
        className={clsx('mt-2', invalid && 'border-line-negative')}
        disabled={disabled || activeIndex !== undefined}
      >
        {buttonLabel || 'Add'}
      </Button>
    </div>
  )
}
